import axios from 'axios';
import get from 'lodash/get';
import has from 'lodash/has';
import set from 'lodash/set';
import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import intersectionBy from 'lodash/intersectionBy';
import { canView } from '@/libs/aclMiddleware';
import { getUrl } from '@/config/api.routes';
import { getFile, applyReplyMessages, composeMessage } from '@/libs';
import { SIDE_PANEL_TAB_NAMES, MESSAGES_LIMIT, ALL_CONVERSATIONS_LIMIT, NOTES_DISPLAY_FREQUENCY_OPTIONS, NOTE_TYPES } from '@/constants';
import { NOTES, TEAMS, CATEGORIES, TAGS, KNOWLEDGEBASE, QUICK_RESPONSE } from '@/constants/analytics';
import { BOT_ID } from '@/constants/agent';
import { Events } from '@/libs';
import { buildEncodedUrl } from '@/libs/urlencoding';
import * as log from 'loglevel';

const LIST_LIMIT = 25;
const MAX_ITEM_LIMIT = 999999;
const TAG = '[STORE][CONVERSATIONS]';

export default {
  getAttachments({ state, commit, rootState }, { customerId, limit, offset, mode }) {
    const hide_deleted = !canView(rootState.agent.permissions, '/conversations/message/audit');
    return axios.get(`${getUrl('conversations.customers')}/${customerId}/attachments`, {
      params: { limit, offset, types: mode.types, ...(hide_deleted && { hide_deleted }) },
    }).then(({ data }) => {
      data.models = offset ?
        [...state.attachmentsHistory.records.models, ...data.models]
        : data.models;
      commit('SET_ATTACHMENTS_HISTORY', { records: data, loaded: true, mode });
    });
  },

  async getConversationById(_, { conversationId }) {
    return (await axios.get(`${getUrl('conversations.conversations')}/${conversationId}`)).data;
  },

  // todo (Gabe) - merge this with selectConversation
  getCustomerCoversationsFromMessages({ commit, rootGetters, rootState, state }, { customer, params }) {
    commit('CUSTOMER_CONVERSATIONS_LOADING', true);

    let endpoint = `${getUrl('conversations.customers')}/${customer.id}/messages`;
    let messagesPath = 'data.models';
    let totalMessagesPath = 'data.pagination.rowCount';

    if (rootState.featureFlags.FEATURE_FLAGS.SCROLL_TO_CONVERSATION) {
      endpoint = `${getUrl('conversations.customers')}/${customer.id}/bidirectional_messages`;
      messagesPath = 'data.results';
      totalMessagesPath = 'data.pageInfo.total';
    }
    return axios.get(endpoint, { params: { ...params, events: true } })
      .then(async data => {
        const messages = get(data, messagesPath, []);
        const conversations = rootGetters['conversations/conversations'];

        /*
          Filter out conversation_id's to only retrive conversations
          for loaded (paginated) messages
        */
        let ids = [...new Set(messages.map(x => x.conversation_id))];
        ids = ids.filter(id => !conversations.some(c => c.id === id));
        const conversationsParams = { ids, lm: false };

        let promise;
        if (ids.length) {
          promise = axios.get(`${getUrl('conversations.customers')}/${customer.id}/conversations`, { params: conversationsParams });
        } else {
          promise = Promise.resolve();
        }

        return [data, await promise];
      })
      .then(([messagesResult, conversationsResult]) => {
        commit('CUSTOMER_CONVERSATIONS_LOADING', false);
        const messages = get(messagesResult, messagesPath, []);
        const conversations = get(conversationsResult, 'data.models', []);
        const agentsById = rootGetters['agents/agentsById'];
        commit('TOTAL_CUSTOMER_MESSAGES', get(messagesResult, totalMessagesPath));
        if (rootState.featureFlags.FEATURE_FLAGS.SCROLL_TO_CONVERSATION) {
          const updatedPageInfo = state.messagesPageInfo;
          updatedPageInfo.isFirstConversationLoad = false;
          updatedPageInfo.total = get(messagesResult, 'data.pageInfo.total');
          if (params.direction === 'next') {
            updatedPageInfo.hasNext = get(messagesResult, 'data.pageInfo.hasNext');
            updatedPageInfo.next = get(messagesResult, 'data.pageInfo.next');
          } else {
            updatedPageInfo.hasPrevious = get(messagesResult, 'data.pageInfo.hasPrevious');
            updatedPageInfo.previous = get(messagesResult, 'data.pageInfo.previous');
          }
          commit('SET_MESSAGES_PAGE_INFO', updatedPageInfo);
          commit('ADD_CONVERSATIONS_AND_MESSAGES', { conversations, messages, agentsById, customer, direction: params.direction });
        } else {
          commit('ADD_CONVERSATIONS', { conversations, messages, agentsById, customer });
        }
      }).catch(() => {
        commit('CUSTOMER_CONVERSATIONS_LOADING', false);
      });
  },

  sendMessage({ commit }, { id, ...message }) {
    const composedMessage = composeMessage(message);
    const postUrl = `${getUrl('conversations.conversations', id)}/messages`;
    return axios.post(postUrl, composedMessage)
      .then(data => {
        commit('ADD_MESSAGE', data.data);
        return data.data;
      });
  },

  appendNewMessage({ commit, state, rootState, dispatch }, message) {
    // append message based on customer & conversation id
    if (state.selected.customer_id === message.conversation.customer_id) {
      commit('ADD_MESSAGE', message);
      Events.emit('new.message');

      if (!isEqual(state.selected.ai_state, message.conversation.ai_state)) {
        const updatedConversation = {
          ...state.selected,
          ai_state: message.conversation.ai_state,
        };
        commit('UPDATE_SELECTED_CONVERSATION', [updatedConversation, rootState.agent.profile]);
      }
    }
    // Update LeftPanel
    const agents = get(message, 'rooms.0.agents', []);
    const teams = get(message, 'rooms.0.teams', []);
    const customer = get(message, 'rooms.0.customer', {});
    const conversation = get(message, 'conversation');
    message = omit(message, ['conversation']);
    return dispatch('updateLeftPanelStateInRealtime', {
      conversation,
      agents,
      message,
      customer,
      teams,
    });
  },

  updateAgentAssignment({ commit, state, dispatch }, { conversation, rooms }) {
    const customerId = get(conversation, 'customer_id');
    const agents = get(conversation, 'agents', []);
    if (state.selected.customer_id === customerId) {
      // update current selected conversation if customer matches
      commit('UPDATE_AGENTS', [conversation.id, agents, conversation.customer_id]);
    }

    const assignedAgents = get(rooms, '0.agents', []);
    const teams = get(rooms, '0.teams', []);
    const customer = get(rooms, '0.customer', {});
    return dispatch('updateLeftPanelStateInRealtime', {
      conversation,
      agents: assignedAgents,
      customer,
      teams,
    });
  },

  /**
   * To support realtime update on conversation panel
   *  - new assignment
   *  - new message
   *
   *  @return {Boolean} whether the left panel state is updated
   */
  updateLeftPanelStateInRealtime({ state, commit, rootState, getters }, { conversation, message, agents = [], teams = [], customer = {} }) { // eslint-disable-line
    log.info(TAG, 'updateLeftPanelStateInRealtime called with:', getters.shouldUpdateConversationsInRealtime, conversation.id);
    // If there is filters set, do not update in real-time
    if (!getters.shouldUpdateConversationsInRealtime) {
      return false;
    }

    const currentTab = state.selectedTab;
    let shouldUpdateConversationsState = false;

    if (currentTab === 'mine') {
      // Check if my agent is assigned into this conversation
      const me = agents.find(agent => agent.id === rootState.agent.profile.id);
      shouldUpdateConversationsState = !!me;
    } else if (currentTab === 'team') {
      const watchTeams = get(rootState, 'agent.profile.watchableTeams', []);
      // Check if the message is under watch team.
      shouldUpdateConversationsState = intersectionBy(watchTeams, teams, (t) => t.id).length > 0;
    } else if (currentTab === 'queue') {
      shouldUpdateConversationsState = conversation.queued;
    } else if (currentTab === 'all') {
      // always true
      shouldUpdateConversationsState = true;
    } else {
      // Do not update panel
      return false;
    }

    log.debug(TAG, 'shouldUpdateConversationsState:', shouldUpdateConversationsState, message);
    if (shouldUpdateConversationsState && message && conversation) {
      const foundConversation = get(
        state.conversationsByConversationId,
        conversation.id,
        // Double check if the conversation doesn't exist by customer id
        // this is to prevent duplicate conversation in left panel with new conversation id
        get(getters.conversationsByCustomerId, conversation.customer_id, null));

      // construct leftpanel conversation item
      set(conversation, 'unread_message_count', 0);

      if (message.sender_type === 'customers') {
        set(conversation, 'oldest_unanswered_customer_message_at', message.created_at);
        set(conversation, 'latest_customer_message_at', message.created_at);
        set(conversation, 'unread_message_count', 1);
        set(conversation, 'effective_status', conversation.status);
        set(conversation, 'latest_message_at', message.created_at);
        set(conversation, 'latest_message', message);
      } else if (message.sender_type === 'agents' && message.sender_id !== BOT_ID) {
        set(conversation, 'oldest_unanswered_customer_message_at', new Date(0).toISOString());
        set(conversation, 'latest_message_at', message.created_at);
        set(conversation, 'latest_message', message);
      }

      if (!foundConversation) {
        log.debug(TAG, 'Realtime: New conversation received:', JSON.stringify(conversation));
        if (!isEmpty(customer)) {
          conversation.customer = customer;
        }

        if (getters.conversations.length < 50) { // eslint-disable-line
          // just add it to top
          commit('APPEND_CONVERSATION', [conversation]);
        } else {
          // add to top and remove last one
          commit('APPEND_CONVERSATION', [conversation, getters.conversations[getters.conversations.length - 1]]);
        }

      } else {
        if (foundConversation.oldest_unanswered_customer_message_at
          && foundConversation.oldest_unanswered_customer_message_at !== new Date(0).toISOString()
          && message.sender_type === 'customers') {
          conversation = omit(conversation, 'oldest_unanswered_customer_message_at');
        }

        // Sync unread_message_count
        if (foundConversation.unread_message_count) {
          set(conversation, 'unread_message_count', conversation.unread_message_count + foundConversation.unread_message_count);
        }

        conversation = merge(cloneDeep(foundConversation), conversation);
        // Update last message etc..
        commit('UPDATE_CONVERSATION', [conversation]);
      }
    }

    return true;
  },

  sendCustomerMessage({commit}, payload) {
    payload = {
      conversation: payload.conversation,
      customer: payload.customer,
      message: {
        channel: 'webchat',
        payload: payload.message,
      },
    };

    return axios.post(`${getUrl('conversations.customer_send')}`, payload)
      .then(data => {
        commit('ADD_MESSAGE', data.data);
        return data.data;
      });
  },

  updateMessage(_, { conversationId, messageId, payload }) {
    return axios
      .put(`${getUrl('conversations.conversations', conversationId)}/messages/${messageId}`, payload);
  },

  deleteMessage(_, { conversationId, messageId }) {
    return axios
      .delete(`${getUrl('conversations.conversations', conversationId)}/messages/${messageId}`);
  },

  // todo (Gabe) - verify can be removed
  get(context, [id]) {
    return axios.get(`${getUrl('conversations.customers')}/${id}/conversations`, {params: { limit: ALL_CONVERSATIONS_LIMIT }});
    /**
      .then(data => {
         * Turning this on causes bug that stops All polling, but if we set
         * polling to true that will be wrong too to do that from here. Ideally
         * there shouldn't be a store action that doesn't commit to store, so
         * this whole flow needs to be refactored.
          commit('LOAD_LIST', [
              [data.data],
              rootState.agent.profile,
              isUpdate,
            ]);
            return data;
          });
    */
  },

  unfocus({ commit }) {
    commit('UNFOCUS');
  },

  clearSelectedConversation({ commit }) {
    commit('CLEAR_SELECTED_CONVERSATION');
  },

  focus({ commit }, customer) {
    commit('SET_FOCUS', customer);
  },

  scheduleMessage(params, [id, content, schedule]) {
    return axios.post(`${getUrl('conversations.conversations', id)}/schedule`, {
      content,
      schedule,
    }).then(data => data);
  },

  list({ commit, rootGetters }, params = {}) {
    const agentProfile = rootGetters['agent/profile'];
    const { channel, scope, polling } = params;

    delete params.channel;
    delete params.scope;
    delete params.polling;

    if (!polling) {
      commit('SET_FILTER', params.filter);
    }

    let endpoint = `${getUrl('conversations.conversations', channel)}/${scope}`;
    if (params.filter) {
      endpoint = `${endpoint}/search`;
    }

    // External and configurable conversations filter handle in background
    const hide_closed_conversations = get(agentProfile, 'preferences.hide_closed_conversations', false);
    params = merge(params, {
      filter: { ...(hide_closed_conversations && { hide_closed_conversations }) },
    });

    return axios.get(endpoint, { params })
      .then(data => {
        const conversations = get(data, 'data.models', []);
        const pagination = get(data, 'data.pagination', {});

        const replaceList = params.offset === 0;
        commit('LOAD_LIST', [
          conversations,
          agentProfile,
          replaceList,
          polling,
          params.order,
          scope,
          channel,
        ]);

        let markAsLoaded = false;
        if (pagination.rowCount) {
          markAsLoaded = true;
        }
        if (!polling) {
          commit('SET_PAGINATION', pagination);
        }
        commit('MARK_AS_LOADED', markAsLoaded);
        return data;
      })
      .catch(err => {
        log.error(TAG, 'Error fetching conversations list:', err);
        throw err;
      });
  },

  refreshCounters({ commit, rootGetters }, { channel, scope }) {
    const views = rootGetters['agent/views'];
    const agentProfile = rootGetters['agent/profile'];
    // External and configurable conversations filter handle in background
    const hide_closed_conversations = get(agentProfile, 'preferences.hide_closed_conversations', false);
    const params = {
      scope,
      views,
      ...(hide_closed_conversations && { filter: { hide_closed_conversations } }),
    };
    return axios.get(`${getUrl('conversations.conversations')}/${channel}/stats`, { params })
      .then(data => {
        for (const [key, value] of Object.entries(data.data)) {
          commit('UPDATE_SPECIAL_COUNTER', [key, Number(value)]);
        }
        return data;
      })
      .catch(err => {
        log.error(TAG, 'Error refreshing counters:', err);
        throw err;
      });
  },

  setFilter(context, filter) {
    context.commit('SET_FILTER', filter);
  },

  /**
  * Select conversation and load all related data
  *
  * @param {id} selectedConversation
  * @param {customer} selectedCustomer
  * @param {scrollToConversationId} undefined by default. When set, will load messages around this conversation
  */
  selectConversation({
    state,
    commit,
    dispatch,
    rootState,
    rootGetters,
  },
  { id, customer, scrollToConversationId }) {
    const isScrollToConversation = rootState.featureFlags.FEATURE_FLAGS.SCROLL_TO_CONVERSATION;
    const agentsById = rootGetters['agents/agentsById'];
    // todo (Gabe) - evaluate cleaning this up
    if (!id) {
      commit('SELECT_CONVERSATION', [null, get(rootState, 'agent.profile', null)]);
      return Promise.resolve();
    }

    log.debug(TAG, 'Selecting conversation for customer:', id, customer.id, scrollToConversationId);
    let isUnread = false;
    commit('CUSTOMER_CONVERSATIONS_LOADING', true);
    if (customer && customer.id) {
      const conversation = state.conversationsByConversationId[id];
      if (get(conversation, 'unread_message_count')) {
        isUnread = true;
      }
    }

    let endpoint = `${getUrl('conversations.customers')}/${customer.id}/messages`;
    let messagesPath = 'data.models';
    let totalMessagesPath = 'data.pagination.rowCount';
    const params = { limit: MESSAGES_LIMIT, events: true };

    if (isScrollToConversation) {
      endpoint = `${getUrl('conversations.customers')}/${customer.id}/bidirectional_messages`;
      messagesPath = 'data.results';
      totalMessagesPath = 'data.pageInfo.total';
      params.conversation_id = scrollToConversationId;
    }

    return Promise.resolve()
      .then(async() => {
        // Fetch dependent data together
        const selectedCustomerResult = await axios.get(`${getUrl('conversations.customers')}/${customer.id}`);
        const messagesResult = await axios.get(endpoint, { params });
        const messages = get(messagesResult, messagesPath, []);
        const conversationParams = { ids: [...new Set(messages.map(x => x.conversation_id))], lm: false };
        const conversationsResult = await axios.get(
          `${getUrl('conversations.customers')}/${customer.id}/conversations`,
          { params: conversationParams });

        return [selectedCustomerResult, messagesResult, conversationsResult];
      })
      .then(([selectedCustomerResult, messagesResult, conversationsResult]) => {
        const selectedCustomer = get(selectedCustomerResult, 'data', {});
        const messages = get(messagesResult, messagesPath, []);
        const conversations = get(conversationsResult, 'data.models', []);
        const customerId = customer.id;

        const starred = get(state.conversationsByConversationId[id], 'starred', false);
        const conversationIds = conversations.map(c => c.id);

        applyReplyMessages({ messages });

        commit('SET', ['selectedCustomer', selectedCustomer]);
        commit('SET_NOTES', [[], true]);
        commit('TOTAL_CUSTOMER_MESSAGES', get(messagesResult, totalMessagesPath));
        if (isScrollToConversation) {
          const messagesPageInfo = get(messagesResult, 'data.pageInfo', {});
          messagesPageInfo.isFirstConversationLoad = false;
          if (scrollToConversationId) {
            // When a conversation is selected, hasNext and hasPrevious are for the results of the query only
            // So, they're set to true then in the next fetch with no specific conversation are set to the proper value
            messagesPageInfo.hasNext = true;
            messagesPageInfo.hasPrevious = true;
            messagesPageInfo.isFirstConversationLoad = true;
          }
          commit('SET_MESSAGES_PAGE_INFO', get(messagesResult, 'data.pageInfo', {}));
        }
        commit('SELECT_CONVERSATION', [{ conversations, messages, customer: selectedCustomer }, rootState.agent.profile, agentsById, starred]);
        dispatch('notesList', [id, 0, 0, false]);
        dispatch('scheduleList', [customerId, LIST_LIMIT, 0]);
        dispatch('documentsList', [id]);
        dispatch('assetsList', [id]);
        dispatch('feedbackInfo', [customerId]);
        dispatch('conversationReviews/fetchReviews', conversationIds, { root: true });
        dispatch('getHeadsUpNotes', [id, {
          offset: 0,
          limit: MAX_ITEM_LIMIT,
        }]);
        commit('CUSTOMER_CONVERSATIONS_LOADING', false);
        commit('RESET_KB');

        if (isUnread) {
          dispatch('decreaseUnreadCounter');
        }

        return customerId;
      })
      .catch(() => {
        commit('CUSTOMER_CONVERSATIONS_LOADING', true);
      });
  },

  updateNewConversationIndicator(context, value) {
    context.commit('UPDATE_NEW_CONVERSATION_INDICATOR', value);
  },

  updateIsConversationOpen(context, value) {
    context.commit('IS_COVERSATION_OPEN', value);
  },

  updateIsVideoContext(context, value) {
    context.commit('IS_VIDEO_CONTEXT', value);
  },

  updateIsCobrowseContext(context, value) {
    context.commit('IS_COBROWSE_CONTEXT', value);
  },

  updateVoiceCallContext(context, value) {
    context.commit('IS_VOICE_CONTEXT', value);
  },

  readConversation(context, id) {
    const payload = {
      event: {
        action: 'messages.mark.read',
        content: '',
      },
      message_type: 'event',
    };

    return axios.post(`${getUrl('conversations.conversations')}/${id}/events`, payload)
      .then(data => {
        context.commit('MARK_AS_READ', [{ id }, get(context, 'rootState.agent.profile', null)]);
        return data;
      });
  },

  notesList({ commit }, [conversation, limit, offset, include_expired = false, type]) {
    return axios.get(`${getUrl('conversations.conversations', conversation)}/notes`, {
      params: { limit, offset, type, include_expired },
    }).then(data => {
      commit('SET_NOTES', [data.data.models, offset === 0]);
      commit('SET_NOTES_PAGINATION', data.data.pagination);
      return data;
    });
  },

  async getNotes(_, [conversationId, params]) {
    const res = await axios.get(`${getUrl('conversations.conversations', conversationId)}/notes`, {
      params,
    });
    return res;
  },

  async getHeadsUpNotes({ commit }, [conversationId, params]) {
    const res = await axios.get(`${getUrl('conversations.conversations', conversationId)}/notes/heads_up`, {
      params,
    });
    commit('SET_READY_AGENT_NOTES', res.data.models);
    return res;
  },

  scheduleList({ commit }, [customerId, limit, offset]) {
    return axios.get(`${getUrl('management.agents', 'me')}/customers/${customerId}/schedules`, {
      params: { limit, offset },
    }).then(data => {
      commit('SET_SCHEDULES', [data.data.models, offset === 0]);
      return data;
    });
  },

  addNote(
    { commit, rootState, dispatch },
    [
      conversation,
      content,
      type = NOTE_TYPES.STANDARD.key,
      expire_date = null,
      display_frequency = NOTES_DISPLAY_FREQUENCY_OPTIONS.CONTINUOUSLY.key,
    ],
  ) {
    return axios.post(`${getUrl('conversations.conversations', conversation)}/notes`, {
      content,
      type,
      expire_date,
      display_frequency,
    }).then(data => {
      commit('ADD_NOTE', [conversation, data.data]);
      dispatch(
        'notifications/emitEventToSocket',
        { name: NOTES.ADDED,
          payload: {
            agent: get(rootState, 'agent.profile'),
            note: content,
            conversation_id: conversation },
        },
        { root: true },
      );
      return data;
    });
  },

  async updateNote({ rootState, dispatch }, [conversationId, noteId, params]) {
    const res = await axios.put(
      `${getUrl('conversations.conversations', conversationId)}/notes/${noteId}`,
      params,
    );
    dispatch(
      'notifications/emitEventToSocket',
      { name: NOTES.ADDED,
        payload: {
          agent: get(rootState, 'agent.profile'),
          note: params.content,
          conversation_id: conversationId },
      },
      { root: true },
    );
    return res;
  },

  removeNote({ commit, rootState, dispatch }, [conversation, note]) {
    return axios.delete(`${getUrl('conversations.conversations', conversation)}/notes/${note}`).then(data => {
      commit('REMOVE_NOTE', [conversation, note]);
      dispatch(
        'notifications/emitEventToSocket',
        { name: NOTES.REMOVED,
          payload: {
            agent: get(rootState, 'agent.profile'),
            conversation_id: conversation,
            note_id: note,
          },
        },
        { root: true },
      );
      return data;
    });
  },

  async documentsList({ commit }, [conversationId]) {
    const ret = await axios.get(`${getUrl('conversations.conversations', conversationId)}/suggestions/documents`);
    commit('SET_SUGGEST_ITEM_HISTORY_BY_MESSAGE', [ret.data.models, 'documents']);
    return ret.data.models;
  },

  async assetsList({ commit }, [conversationId]) {
    const ret = await axios.get(`${getUrl('conversations.conversations', conversationId)}/suggestions/assets`);
    commit('SET_SUGGEST_ITEM_HISTORY_BY_MESSAGE', [ret.data.models, 'assets']);
    return ret.data.models;
  },

  async feedbackInfo({ commit }, [customerId]) {
    const ret = await axios.get(`${getUrl('conversations.customers')}/${customerId}/last_survey_info`);
    commit('SET_LAST_FEEDBACK_INFO', ret.data);
    return ret.data.models;
  },

  starConversation({ commit }, [id, value]) {
    return (value
      ? axios.post(`${getUrl('management.agents', 'me')}/conversations`, {
        conversation: {
          id,
        },
      })
      : axios.delete(`${getUrl('management.agents', 'me')}/conversations/${id}`))
      .then(data => {
        commit('STAR_CONVERSATION', [id, value]);
        return data;
      });
  },

  updateTags({ commit, rootState, dispatch }, [conversation, tags, updateType]) {
    return axios
      .put(`${getUrl('conversations.conversations', conversation)}/tags`, tags)
      .then(data => {
        commit('UPDATE_TAGS', [conversation, data.data]);
        dispatch(
          'notifications/emitEventToSocket',
          { name: TAGS.UPDATED,
            payload: {
              agent: get(rootState, 'agent.profile'),
              tags,
              conversation_id: conversation,
              updateType,
            },
          },
          { root: true },
        );
        return data;
      });
  },

  updateCategories({ commit, rootState, dispatch }, [conversation, categories, updateType]) {
    return axios
      .put(`${getUrl('conversations.conversations', conversation)}/categories`, categories)
      .then(data => {
        commit('UPDATE_CATEGORIES', [conversation, data.data]);
        dispatch(
          'notifications/emitEventToSocket',
          { name: CATEGORIES.UPDATED,
            payload: {
              agent: get(rootState, 'agent.profile'),
              categories,
              conversation_id: conversation,
              updateType,
            },
          },
          { root: true },
        );
        return data;
      });
  },

  updateTeams({ commit, rootState, dispatch }, [conversation, teams, updateType]) {
    return axios
      .put(`${getUrl('conversations.conversations', conversation)}/teams`, teams)
      .then(data => {
        commit('UPDATE_TEAMS', [conversation, data.data]);
        dispatch(
          'notifications/emitEventToSocket',
          { name: TEAMS.UPDATED,
            payload: {
              agent: get(rootState, 'agent.profile'),
              teams,
              conversation_id: conversation,
              updateType,
            },
          },
          { root: true },
        );
        return data;
      });
  },

  updateAgents({ commit }, [conversation, agents]) {
    return axios
      .put(`${getUrl('conversations.conversations', conversation)}/agents`, agents)
      .then(data => {
        commit('UPDATE_AGENTS',
          [conversation, get(data, 'data.agents', [])]);
        return data;
      });
  },

  assignPrimaryAgent({ commit }, [conversation, payload]) {
    return axios
      .put(`${getUrl('conversations.conversations', conversation)}/attachprimary`, payload)
      .then(data => {
        commit('UPDATE_AGENTS',
          [conversation, get(data, 'data.agents', [])]);
        return data;
      });
  },

  lockToPrimaryAgent({ commit }, [conversation, payload]) {
    return axios
      .put(`${getUrl('conversations.conversations', conversation)}/lockprimary`, payload)
      .then(data => {
        commit('UPDATE_LOCK_STATUS', [get(data, 'data'), get(data, 'locked_by.profile', {})]);
        return data;
      });
  },

  fetchCustomerByPhone({ _commit }, phone) {
    return axios.get(`${getUrl('conversations.customers', 'externals')}/sms/${phone}`);
  },

  fetchCustomerByExternalId(_, externalId) {
    return axios.get(`${getUrl('conversations.customers', 'externals')}/external_id/${externalId}`);
  },

  search(_, queryParams) {
    return axios.get(`${getUrl('conversations.conversations', 'all')}/all/search`, { params: queryParams });
  },

  createNewSMSCustomer({ commit }, payload) {
    return axios.post(`${getUrl('management.channels')}/sms/customers`, payload)
      .then(data => {
        commit('CREATE_CONVERSATION', data.data);
        return data;
      });
  },

  updateCustomer({ commit, getters }, [id, customer]) {
    return axios
      .put(getUrl('conversations.customers', id), customer)
      .then(data => {
        commit('UPDATE_CUSTOMER', [data.data, getters.conversationsByCustomerId[data.data.id]]);
        commit('messages/UPDATE_MESSAGES_SENDER', data.data, { root: true });
        return data;
      });
  },

  async updateCustomerProfileImage({ commit, state, getters }, [id]) {
    const result = await axios.put(`${getUrl('conversations.customers', id)}/profile_image`);
    const updatedCustomer = merge(
      {},
      get(state, 'customer', {}),
      get(result, 'data', {}),
    );
    commit('UPDATE_CUSTOMER', [updatedCustomer, getters.conversationsByCustomerId[updatedCustomer.id]]);
    commit('messages/UPDATE_MESSAGES_SENDER', updatedCustomer, { root: true });
    return updatedCustomer;
  },

  closeConversation({ commit }, { id, resolved_status }) {
    return axios
      .put(`${getUrl('conversations.conversations', id)}/status`, {
        value: 'closed',
        resolved_status,
      })
      .then(data => {
        commit('CLOSE_CONVERSATION', data.data);
        return data;
      });
  },

  exportConversation(context, id) {
    // Todo (Gabe) Remove from store actions
    return axios
      .get(`${getUrl('conversations.conversations', id)}/export`, {
        headers: {
          accept: 'text/csv',
        },
      })
      .then(getFile);
  },

  async downloadConversationTranscript({ rootState, rootGetters, dispatch }, { customerId, startDate, endDate }) {
    const hide_deleted = !canView(rootState.agent.permissions, '/conversations/message/audit');
    const params = {
      events: true,
      ...(hide_deleted && { hide_deleted }),
      ...(startDate && { start_date: startDate }),
      ...(endDate && { end_date: endDate }),
    };

    if (isEmpty(rootGetters['agents/agentsById'])) {
      await dispatch('agents/getAgentsList', [{ limit: MAX_ITEM_LIMIT }], { root: true });
    }

    const [messagesResponse, customerResponse] = await Promise.all([
      axios.get(`${getUrl('conversations.customers')}/${customerId}/messages_transcript`, { params }),
      axios.get(`${getUrl('conversations.customers')}/${customerId}`),
    ]);

    return {
      messages: messagesResponse.data,
      customer: customerResponse.data,
      agentsById: rootGetters['agents/agentsById'],
    };
  },

  lockConversation({ commit, rootState }, id) {
    return axios
      .put(`${getUrl('conversations.conversations', id)}/lock`, {})
      .then(data => {
        commit('UPDATE_LOCK_STATUS', [get(data, 'data'), rootState.agent.profile]);
        commit('agent/LOCK_CONVERSATION', id, { root: true });
        return data;
      });
  },

  unlockConversation({ commit, rootState }, id) {
    return axios
      .delete(`${getUrl('conversations.conversations', id)}/lock`)
      .then(data => {
        commit('UPDATE_LOCK_STATUS', [get(data, 'data'), rootState.agent.profile]);
        commit('agent/UNLOCK_CONVERSATION', id, { root: true });
        return data;
      });
  },

  botOn({ commit, rootState }, id) {
    return axios
      .put(`${getUrl('conversations.conversations', id)}/bot_status`, {
        value: 'enabled',
      }).then(data => {
        commit('UPDATE_LOCK_STATUS', [get(data, 'data'), rootState.agent.profile]);
        return data;
      });
  },

  setSystemMessagesDisplay({ commit }, display) {
    commit('SET_SYSTEM_MESSAGES_DISPLAY', display);
  },

  toggleIsTimestampAbsolute({ state, commit }) {
    commit('SET_IS_TIMESTAMP_ABSOLUTE', !state.isTimestampAbsolute);
  },

  runDialog(context, [id, dialog]) {
    // Todo (Gabe) Remove from store actions
    return axios
      .put(`${getUrl('conversations.conversations', id)}/dialog`, {
        id: dialog.id,
        name: dialog.name,
      }).then(data => data);
  },

  runWorkflow(context, [id, workflow]) {
    // Todo (Gabe) Remove from store actions
    return axios
      .put(`${getUrl('conversations.conversations', id)}/workflow`, {
        id: workflow.id,
        name: workflow.name,
      }).then(data => data);
  },

  selectSuggestions({ commit }, payload) {
    commit('SELECT_SUGGESTIONS', payload);
    commit('SET_KB_FROM_SUGGESTIONS', payload);
    commit('SET_OPENED_SIDEBAR_TAB', SIDE_PANEL_TAB_NAMES.KNOWLEDGE_BASE);
  },
  setKbSuggestions({ commit }, payload) {
    commit('SET_KB_SUGGESTION_MODE', payload);
  },

  getKnowledgeBaseDocuments({ commit, rootState }, params) {
    const [param, searchOffset] = params;
    const url_endpoint = `${getUrl('management.documents')}/knowledgebase`;

    const url = buildEncodedUrl(
      url_endpoint,
      param,
    );
    if (rootState.conversations.kbSuggestionMode) {
      return [];
    }

    return axios.get(url).then((ret) => {
      //map object url type to text/html mime type for KB documents
      ret.data.models = ret.data.models.map(x => {
        const objectType = get(x, 'meta.object_type');
        if (objectType === 'url') {
          set(x, 'file.payload.mime', 'text/html');
        }
        return x;
      });
      if (!searchOffset) {
        //append to previous list
        commit('SET_KB_DOCUMENTS', [...rootState.conversations.kbDocuments, ...ret.data.models]);
      } else {
        commit('SET_KB_DOCUMENTS', ret.data.models);
      }
      return ret;
    });
  },

  setKBMode({ commit, rootState, dispatch }, payload) {
    commit('SET_KB_MODE', payload);
    dispatch(
      'notifications/emitEventToSocket',
      { name: KNOWLEDGEBASE.TAB_CHANGED,
        payload: {
          agent: get(rootState, 'agent.profile'),
          mode: payload,
        },
      },
      { root: true },
    );
  },

  setScope({ commit }, scope) {
    commit('SET_SCOPE', scope);
  },

  setSelectedTab({ commit }, selectedTab) {
    commit('SET_SELECTED_TAB', selectedTab);
  },

  setChannel({ commit }, channel) {
    commit('SET_CHANNEL', channel);
  },

  setSidePanelTab({ commit, state }, tabName) {
    if (state.openedSidebarTab === tabName) commit('SET_OPENED_SIDEBAR_TAB', null);
    else commit('SET_OPENED_SIDEBAR_TAB', tabName);
  },

  async getQuickResponses({ commit }, params) {
    const data = await axios.get(`${getUrl('management.agents', 'me')}/quickresponses`, { params });
    commit('SET_QUICK_RESPONSES_LIST', { list: data.data.models, isNew: get(params, 'offset', 0) === 0 });
    return data;
  },

  async updateQuickResponse({ rootState, dispatch }, [id, content]) {
    const data = await axios.put(`${getUrl('management.agents', 'me')}/quickresponses/${id}`, { content });
    dispatch(
      'notifications/emitEventToSocket',
      { name: QUICK_RESPONSE.UPDATED,
        payload: {
          agent: get(rootState, 'agent.profile'),
          quick_response: content,
          quick_response_id: id,
        },
      },
      { root: true },
    );
    return data;
  },

  async deleteQuickResponse({ rootState, dispatch }, id) {
    const data = await axios.delete(`${getUrl('management.agents', 'me')}/quickresponses/${id}`);
    dispatch(
      'notifications/emitEventToSocket',
      { name: QUICK_RESPONSE.REMOVED,
        payload: {
          agent: get(rootState, 'agent.profile'),
          quick_response_id: id,
        },
      },
      { root: true },
    );
    return data;
  },

  async pinQuickResponse({ rootState, dispatch }, id) {
    const data = await axios.put(`${getUrl('management.agents', 'me')}/quickresponses/${id}/pin`, { value: true });
    dispatch(
      'notifications/emitEventToSocket',
      { name: QUICK_RESPONSE.PINNED,
        payload: {
          agent: get(rootState, 'agent.profile'),
          quick_response_id: id,
        },
      },
      { root: true },
    );
    return data;
  },

  async unpinQuickResponse({ rootState, dispatch }, id) {
    const data = await axios.put(`${getUrl('management.agents', 'me')}/quickresponses/${id}/pin`, { value: false });
    dispatch(
      'notifications/emitEventToSocket',
      { name: QUICK_RESPONSE.UNPINNED,
        payload: {
          agent: get(rootState, 'agent.profile'),
          quick_response_id: id,
        },
      },
      { root: true },
    );
    return data;
  },

  async createQuickResponse({ state, rootState, dispatch }, { content, is_pinned }) {
    const quickResponse = state.quickResponses.find(e => e.content === content);
    if (quickResponse) {
      return Promise.resolve(false);
    }
    const data = await axios.post(`${getUrl('management.agents', 'me')}/quickresponses`, { content, is_pinned });
    dispatch(
      'notifications/emitEventToSocket',
      { name: QUICK_RESPONSE.CREATED,
        payload: {
          agent: get(rootState, 'agent.profile'),
          quick_response: content,
        },
      },
      { root: true },
    );
    return data;
  },

  sendDmDeepLink(context, [id]) {
    // todo fetch custom dm card message for business, and remove from store actions
    const content = 'Please message us directly';
    return axios.post(`${getUrl('conversations.conversations', id)}/messages?deepLink=true`, { content });
  },

  updatePollLimit(context, value) {
    context.commit('UPDATE_POLL_LIMIT', value);
  },

  updateConversation({ rootState, commit }, conversation) {
    commit('UPDATE_CONVERSATION', [conversation, rootState.agent.profile]);
  },

  removeAgent({ commit, state }, { conversation, agentId, removePrimary }) {
    const endpoint = `${conversation.id}/agents/${agentId}`;
    const updates = [axios.delete(getUrl('conversations.conversations', endpoint))];
    if (removePrimary) {
      const path = `${getUrl('conversations.customers', conversation.customer_id)}/agent`;

      updates.push(axios.delete(path));
    }
    return Promise.all(updates)
      .then(() => {
        const agents = conversation.agents.filter(a => a.id !== agentId);
        commit('UPDATE_AGENTS', [conversation.id, agents]);

        const { selectedCustomer } = state;
        if (removePrimary && selectedCustomer.id === conversation.customer_id) {
          commit('REMOVE_PRIMARY_AGENT', [conversation.id, selectedCustomer]);
        }
      });
  },

  decreaseUnreadCounter({ commit, state }) {
    if (state.counters.unread <= 0) { return }
    // TODO (Gabe) - Decrease stat when selected in all tab
    const valueForScope = state.counters.unread - 1;
    commit('UPDATE_SPECIAL_COUNTER', ['unread', valueForScope]);
  },

  async obfuscateMessage({ commit, state }, message) {
    const customerId = get(state, 'selectedCustomer.id');
    const messageId = get(message, 'id');

    const ret = await axios.put(`${getUrl('conversations.customers')}/${customerId}/messages/${messageId}/obfuscate`);
    commit('REPLACE_MESSAGE', get(ret, 'data'));
    return get(ret, 'data');
  },

  async refreshSchedule({ dispatch, state }) {
    const customerId = get(state, 'selectedCustomer.id');
    if (customerId) {
      return dispatch('scheduleList', [customerId, LIST_LIMIT, 0]);
    }
  },

  // Mark customer read label
  async markConversationReadByCustomer({ state, commit }, { conversation, event }) {
    if (has(conversation, 'customer_id')
      && get(state, 'selectedCustomer.id') === get(conversation, 'customer_id')) {
      commit('UPDATE_CUSTOMER_READ', event);
    }
  },

  async getChannelsList({ commit }) {
    const res = await axios.get(`${getUrl('conversations.conversations')}/channelslist`);
    commit('SET_CHANNELS', res.data);
  },

  async sendSyncRequest(_, { args, mapping }) {
    const apiInfo = get(mapping, 'apiInfo', null);
    if (!apiInfo) {
      throw new Error('Api info is missing');
    }

    const payload = cloneDeep(apiInfo);
    const argsEntities = [];
    for (const [name, path] of Object.entries(payload.request_entities)) {
      const resolved_value = get(args, path);
      if (isEmpty(resolved_value)) {
        throw new Error(`${name} is missing.`);
      }

      // Note: resolved_value needs to be always string but the right type is
      // set from Api backend.
      argsEntities.push({ name, resolved_value: String(resolved_value) });
    }

    set(payload, 'entities', argsEntities);
    return (await axios.post(getUrl('aimanager.apiRequests'), payload)).data;
  },

  async getCustomerById({ _, commit }, { customerId }) {
    const res = await axios.get(`${getUrl('conversations.customers')}/${customerId}/`, { params: { customerId } });
    commit('SET_CUTOMER_FROM_ROUTE', res.data);
  },

  async getConversationIdFromCustomer(_, { customerId }) {
    const res = await axios.get(`${getUrl('conversations.customers')}/${customerId}/conversations`, { params: { customerId } });
    return res.data.models[0];
  },

  async getConversation(_, { conversationId, customerId }) {
    const data = await axios.get(`${getUrl('conversations.customers')}/${customerId}/conversations`, { params: { ids: [conversationId] } });
    return data.data.models[0];
  },

  async updateChannelStataus(_, { channel, payload }) {
    return (await axios.post(`${getUrl('management.channels')}/${channel}/status`, payload)).data;
  },

  async getSummary(_, conversationId) {
    return (await axios.get(`${getUrl('conversations.conversations', conversationId)}/summary`)).data.summary;
  },

  async rejectCall(_, payload) {
    return (await axios
      .put(`${getUrl('conversations.conversations')}/${payload.id}/reject`)).data;
  },
  async endCall(_, payload) {
    return (await axios
      .put(`${getUrl('conversations.conversations')}/${payload.id}/state`)).data;
  },

  async muteCustomer(_, payload) {
    return (await axios
      .put(`${getUrl('conversations.customers')}/mute/${payload.id}`)).data;
  },

  async runPreExecutionForEvent({ state, dispatch, rootState }, [id, eventName]) {
    // The entire logic is wrapped in try/catch to avoid any unhandled exception.
    // runPreExecutionForEvent is optional and should not block the main flow.
    try {
      const eventConfigs = get(rootState, 'settings.UiEventConfig.pre_execution.events', []);
      const event = eventConfigs.find(e => e.name === eventName);

      if (isEmpty(event)) {
        log.debug(TAG, `No pre-execution config found for event: ${eventName}`);
        return;
      }

      log.debug(TAG, `run pre execution event: ${eventName}`, id, event);
      const executionType = get(event, 'execution_type', '');
      if (executionType === 'workflow') {
        const name = get(event, 'payload.name', '');

        if (isEmpty(name)) {
          log.debug(TAG, `No workflow name found in pre-execution config for event: ${eventName}`);
          return;
        }

        await dispatch('runWorkflow', [id, { name }]);

        // Note: Running the workflow always lock to bot. However, if the conversation
        //       Agent is unable to proceed because it's locked to bot, unlock it here.
        //       This behavior might need to be configurable in the future.
        if (get(state.selected, 'locked_by.id', null) === BOT_ID) {
          await dispatch('unlockConversation', id);
        }
        return;
      }
    } catch (err) {
      log.error(TAG, `Error running pre-execution: ${id} ${eventName}`, err);
    }
  },
};
